<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import NAV_MAPPER from '@utils/route-mapper.js'
import { authComputed } from '@state/helpers'
import { createConsumer } from '@common/SocketsService.js'
import JwtService from '@common/JwtService'
import ReportsStatusDropdown from '@components/reports/ReportsStatusDropdown'
import { waitOneSecondAndCallFunc } from '@/src/utils/wait-one-second-and-call-func'
import { logoutUser } from '@common/AuthServiceOidc'

export default {
  components: {
    ReportsStatusDropdown,
  },
  data: () => ({
    primaryNav: [],
    secondaryNav: [],
    consumer: null,
    unconfirmedRequestsCount: 0,
    navBarInitiated: false,
    newDashboardNotification: false,
    newReportStatusNotification: false,
  }),

  computed: {
    ...authComputed,

    ...mapState({ permissions: (state) => state.auth.user.role }),
    ...mapState({ timestamp: (state) => state.reports.timestamp }),

    ...mapGetters('auth', ['currentUser', 'loggedIn']),

    showReportsStatusBadge() {
      return this.permissions.primary.reports_page && this.newReportStatusNotification
    },
  },

  watch: {
    currentUser(currentUser) {
      if (currentUser && !this.navBarInitiated && this.loggedIn) {
        this.initNav()
        if (currentUser.role.primary.unconfirmed_bookings_page) {
          this.handleUnconfirmedCable()
        }
        if (currentUser.role.primary.dashboard_page) {
          this.handleDashboardCable()
        }
        if (currentUser.role.primary.reports_page) {
          this.handleReportsCable()
        }
      }
    },
  },

  methods: {
    ...mapActions('auth', ['logout']),

    unconfirmedPath(path) {
      return path.name === NAV_MAPPER['unconfirmed_bookings_page'].name
    },

    dashboardPath(path) {
      return path.name === NAV_MAPPER['dashboard_page'].name
    },

    initNav() {
      if (!this.permissions) {
        this.primaryNav = []
        this.secondaryNav = []
      } else {
        this.navBarInitiated = true
        this.primaryNav = this.mapRoutes(this.permissions.primary)
        this.secondaryNav = this.mapRoutes(this.permissions.secondary)
        if (this.currentUser.access_to_configuration_page) {
          this.secondaryNav.push({
            name: 'configurations',
            text: 'Configurations',
          })
        }

        /* eslint-disable camelcase */
        if (this.currentUser.access_to_rrms) {
          this.secondaryNav.push({
            href: process.env.VUE_APP_RRMS_URL,
            text: 'RRMS',
          })
        }
        /* eslint-enable camelcase */
      }
    },

    mapRoutes(permissions) {
      if (!permissions) return []
      const routeKeys = []
      const keys = Object.keys(permissions)
      const values = Object.values(permissions)

      values.forEach((value, index) => {
        if (value && Object.keys(NAV_MAPPER).includes(keys[index])) {
          routeKeys.push(NAV_MAPPER[keys[index]])
        }
      })

      return routeKeys
    },

    showUnconfirmedBadge(path) {
      return this.unconfirmedPath(path) && this.unconfirmedRequestsCount > 0
    },

    showDashboardBadge(path) {
      return this.dashboardPath(path) && this.newDashboardNotification
    },

    handleUnconfirmedCable() {
      const that = this

      this.consumer = createConsumer({
        channel: 'UnconfirmedChannel',
        handler: {
          received(data) {
            that.unconfirmedRequestsCount = data
          },
          disconnected() {
            if (JwtService.getToken()) waitOneSecondAndCallFunc(that.handleUnconfirmedCable)
            else logoutUser()
          },
        },
      })
    },

    handleDashboardCable() {
      const that = this

      this.consumer = createConsumer({
        channel: 'DashboardChannel',
        handler: {
          received(data) {
            that.newDashboardNotification = data.message
          },
          disconnected() {
            if (JwtService.getToken()) waitOneSecondAndCallFunc(that.handleDashboardCable)
            else logoutUser()
          },
        },
      })
    },

    handleReportsCable() {
      const that = this

      this.consumer = createConsumer({
        channel: 'ReportsChannel',
        params: { timestamp: that.timestamp },
        handler: {
          received(data) {
            if (data.status === 'success') {
              that.newReportStatusNotification = true
            } else if (data.status === 'error') {
              that.$notify({
                title: 'Error',
                text: data.message,
                type: 'error',
              })
            }
          },
          disconnected() {
            if (JwtService.getToken()) waitOneSecondAndCallFunc(that.handleReportsCable)
            else logoutUser()
          },
        },
      })
    },
  },
}
</script>

<template>
  <nav class="navbar-expand-md">
    <b-navbar
      type="dark"
      variant="primary"
      toggleable
      class="pl-3 pr-4"
    >
      <b-navbar-nav>
        <b-navbar-brand class="d-flex">
          <h5 class="my-auto ml-2"> Passenger Assist </h5>
        </b-navbar-brand>
      </b-navbar-nav>

      <b-navbar-nav class="m-auto">
        <b-nav-item
          v-for="path in primaryNav"
          :key="path.text"
          :to="{ name: path.name }"
          class="mr-2 position-relative"
        >
          <BaseIcon
            class="ml-3 mr-2 text-white"
            :name="path.icon"
          />
          <span class="font-weight-bold text-white"> {{ path.text }} </span>
          <span
            v-if="showUnconfirmedBadge(path)"
            class="dot unconfirmed-notification-wrapper text-white"
          >
            {{ unconfirmedRequestsCount }}
          </span>
          <span
            v-if="showDashboardBadge(path)"
            class="dot dashboard-notification-wrapper"
          />
        </b-nav-item>
      </b-navbar-nav>
      <b-navbar-nav
        v-if="permissions"
        class="text-center"
      >
        <b-nav-item>
          <div class="d-flex">
            <ReportsStatusDropdown @removeBadge="newReportStatusNotification = false" />
            <span
              v-if="showReportsStatusBadge"
              class="dot report-notification-wrapper"
            />
          </div>
        </b-nav-item>
        <b-nav-item
          class="my-auto"
          data-cy="logout"
          @click="logout()"
        >
          <BaseIcon
            class="text-white"
            name="sign-out-alt"
          />
          <span class="text-white"> Logout </span>
        </b-nav-item>
        <li
          v-if="secondaryNav.length > 0"
          class="nav-item my-auto ml-2 px-2"
        >
          <b-dropdown
            right
            toggle-class="btn btn-dark"
          >
            <template #button-content>
              <BaseIcon name="cog" />
              Menu
            </template>
            <b-dropdown-item
              v-for="path in secondaryNav"
              :key="path.text"
              :href="path.href || undefined"
              :to="path.href ? undefined : { name: path.name }"
              :target="path.href ? '_blank' : undefined"
              :rel="path.href ? 'noopener noreferrer' : undefined"
            >
              <p class="my-auto font-weight-bold"> {{ path.text }} </p>
            </b-dropdown-item>
          </b-dropdown>
        </li>
      </b-navbar-nav>
    </b-navbar>
  </nav>
</template>

<style lang="scss">
@import '@variables';
// stylelint-disable
.logo-wrapper {
  width: 50px;
  height: 45px;
}

nav {
  min-height: 69px;
}

.dashboard-notification-wrapper {
  width: 22px;
  height: 22px;
}

.report-notification-wrapper {
  position: relative !important;
  right: 16%;
  width: 15px;
  height: 15px;
}

.unconfirmed-notification-wrapper {
  width: auto;
  min-width: 22px;
  height: auto;
  min-height: 22px;
  padding: 0 3px 0 3px;
  font-size: 0.75rem;
  font-weight: bold;
  text-align: center;
}

.dot {
  position: absolute;
  top: 15%;
  background-color: $notification-dot;
  border: 3px solid white;
  border-radius: 10px;
}

.dashboard-notification-wrapper,
.unconfirmed-notification-wrapper {
  top: -0.25rem;
  right: -1rem;
}
</style>
